import { useState, useEffect } from 'react'
import { PlusIcon, EyeIcon, PlayIcon, PauseIcon, ClipboardIcon, UserGroupIcon, TrashIcon } from '@heroicons/react/24/outline'
import api from '@/services/api'
import CreateReferralCodeModal from '@/components/modals/CreateReferralCodeModal'
import useAuthStore from '@/stores/authStore'

export default function ReferralCodesPage() {
  const { user: currentUser } = useAuthStore()
  const [referralCodes, setReferralCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [statistics, setStatistics] = useState(null)
  const [selectedCode, setSelectedCode] = useState(null)
  const [showUsersModal, setShowUsersModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchReferralCodes()
    fetchStatistics()
  }, [])

  const fetchReferralCodes = async () => {
    try {
      setLoading(true)
      const response = await api.get('/referral-codes')
      
      if (response.data.success) {
        setReferralCodes(response.data.data.referralCodes || [])
      }
    } catch (error) {
      console.error('Failed to fetch referral codes:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/referral-codes/statistics')
      if (response.data.success) {
        setStatistics(response.data.data.overview)
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error)
    }
  }

  const handleCreateSuccess = () => {
    fetchReferralCodes()
    fetchStatistics()
    alert('담당자코드가 성공적으로 생성되었습니다!')
  }

  const toggleCodeStatus = async (code, currentStatus) => {
    try {
      await api.put(`/referral-codes/${code}/status`, {
        isActive: !currentStatus
      })
      fetchReferralCodes()
    } catch (error) {
      console.error('Failed to update referral code status:', error)
      alert('담당자코드 상태 업데이트에 실패했습니다.')
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('클립보드에 복사되었습니다!')
    }).catch(err => {
      console.error('Failed to copy:', err)
    })
  }

  const viewRegisteredUsers = (code) => {
    setSelectedCode(code)
    setShowUsersModal(true)
  }

  const handleDeleteCode = async (code) => {
    setDeleting(true)
    try {
      const response = await api.delete(`/referral-codes/${code}`)
      if (response.data.success) {
        alert('담당자코드가 성공적으로 삭제되었습니다.')
        setDeleteConfirm(null)
        fetchReferralCodes()
        fetchStatistics()
      }
    } catch (error) {
      console.error('Failed to delete referral code:', error)
      const errorMessage = error.response?.data?.error?.message || '담당자코드 삭제에 실패했습니다.'
      alert(errorMessage)
    } finally {
      setDeleting(false)
    }
  }

  const getStatusLabel = (isActive) => {
    return isActive ? '활성' : '비활성'
  }

  const getStatusColor = (isActive) => {
    return isActive 
      ? 'bg-green-100 text-green-800' 
      : 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">담당자코드 관리</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          새 코드 생성
        </button>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-600">전체 코드</h3>
            <p className="text-2xl font-bold text-gray-900">{statistics.total_codes}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-600">활성 코드</h3>
            <p className="text-2xl font-bold text-green-600">{statistics.active_codes}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-600">총 가입자</h3>
            <p className="text-2xl font-bold text-blue-600">{statistics.total_registrations}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-600">평균 사용률</h3>
            <p className="text-2xl font-bold text-purple-600">
              {Number(statistics.avg_usage_per_code || 0).toFixed(1)}
            </p>
          </div>
        </div>
      )}

      {/* Referral Codes Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                코드
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                이름
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                생성자
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                사용 횟수
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                상태
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                생성일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {referralCodes.map((code) => (
              <tr key={code.code} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-medium bg-gray-100 px-2 py-1 rounded">
                      {code.code}
                    </span>
                    <button
                      onClick={() => copyToClipboard(code.code)}
                      className="text-gray-400 hover:text-gray-600"
                      title="복사"
                    >
                      <ClipboardIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className="font-medium">{code.name || '-'}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div>
                    <div className="font-medium">{code.createdByAdmin?.name}</div>
                    <div className="text-gray-500">@{code.createdByAdmin?.username}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                  <div className="flex items-center gap-2">
                    <span>{code.usageCount}</span>
                    {code.usageCount > 0 && (
                      <button
                        onClick={() => viewRegisteredUsers(code)}
                        className="text-blue-600 hover:text-blue-800"
                        title="가입자 보기"
                      >
                        <UserGroupIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(code.isActive)}`}>
                    {getStatusLabel(code.isActive)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(code.createdAt).toLocaleDateString('ko-KR')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => toggleCodeStatus(code.code, code.isActive)}
                      className={`${
                        code.isActive 
                          ? 'text-orange-600 hover:text-orange-900' 
                          : 'text-green-600 hover:text-green-900'
                      }`}
                      title={code.isActive ? '비활성화' : '활성화'}
                    >
                      {code.isActive ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
                    </button>
                    {currentUser?.role === 'super_admin' && (
                      <button
                        onClick={() => setDeleteConfirm(code)}
                        className="text-red-600 hover:text-red-900"
                        title="삭제"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {referralCodes.length === 0 && (
        <div className="text-center py-12">
          <UserGroupIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">담당자코드가 없습니다.</p>
        </div>
      )}

      {/* Users Modal */}
      {showUsersModal && selectedCode && (
        <RegisteredUsersModal
          isOpen={showUsersModal}
          onClose={() => {
            setShowUsersModal(false)
            setSelectedCode(null)
          }}
          referralCode={selectedCode}
        />
      )}

      {/* Create Referral Code Modal */}
      <CreateReferralCodeModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                담당자코드 삭제 확인
              </h3>
              <p className="text-gray-600">
                정말로 <strong>{deleteConfirm.code}</strong> 코드를 삭제하시겠습니까?
              </p>
              {deleteConfirm.usageCount > 0 && (
                <p className="text-red-600 text-sm mt-2">
                  이 코드로 가입한 {deleteConfirm.usageCount}명의 사용자가 있습니다.
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => handleDeleteCode(deleteConfirm.code)}
                disabled={deleting}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? '삭제 중...' : '삭제'}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Registered Users Modal Component
function RegisteredUsersModal({ isOpen, onClose, referralCode }) {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 })

  useEffect(() => {
    if (isOpen && referralCode) {
      fetchRegisteredUsers()
    }
  }, [isOpen, referralCode, pagination.page])

  const fetchRegisteredUsers = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/referral-codes/${referralCode.code}/users`, {
        params: {
          page: pagination.page,
          limit: 10
        }
      })
      
      if (response.data.success) {
        setUsers(response.data.data.users || [])
        setPagination(prev => ({
          ...prev,
          totalPages: response.data.data.pagination?.totalPages || 1
        }))
      }
    } catch (error) {
      console.error('Failed to fetch registered users:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <UserGroupIcon className="h-5 w-5" />
            담당자코드: {referralCode.code} - 가입자 목록
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <EyeIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Code Info */}
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">총 가입자:</span>
              <span className="ml-2 font-medium">{referralCode.usageCount}명</span>
            </div>
            <div>
              <span className="text-gray-600">생성일:</span>
              <span className="ml-2 font-medium">
                {new Date(referralCode.createdAt).toLocaleDateString('ko-KR')}
              </span>
            </div>
            <div>
              <span className="text-gray-600">생성자:</span>
              <span className="ml-2 font-medium">
                {referralCode.createdByAdmin?.nickname} (@{referralCode.createdByAdmin?.username})
              </span>
            </div>
            <div>
              <span className="text-gray-600">상태:</span>
              <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                referralCode.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {referralCode.isActive ? '활성' : '비활성'}
              </span>
            </div>
          </div>
        </div>

        {/* Users List */}
        <div className="flex-1 overflow-hidden">
          <h4 className="font-medium text-gray-900 mb-3">
            가입자 목록
          </h4>
          
          <div className="overflow-y-auto max-h-96">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <UserGroupIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>아직 가입자가 없습니다.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {users.map((user, index) => (
                  <div
                    key={user.id}
                    className="bg-gray-50 p-3 rounded-lg border-l-4 border-blue-500"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-gray-900">
                          {user.nickname}
                        </div>
                        <div className="text-sm text-gray-600">
                          @{user.username}
                        </div>
                        {user.name && (
                          <div className="text-sm text-gray-500">
                            {user.name}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(user.createdAt).toLocaleDateString('ko-KR')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="mt-4 flex justify-center">
            <div className="flex space-x-2">
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setPagination(prev => ({ ...prev, page }))}
                  className={`px-3 py-1 rounded ${
                    pagination.page === page
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Close Button */}
        <div className="mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  )
}